import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router-dom';

import type { IClientRecentResources } from '.';

import { ResourcesItemsCard } from '.';
import { ResourceAnnouncement, ResourceDatabase, ResourceDocument, ResourceLink } from '@/assets/icons';
import { ButtonSeeMore } from '@/components/common';
import { ResourceTypeId } from '@/store/enums';
import { ResourcesDatabaseItemsCard } from './ResourcesDatabaseItemsCard';
import routes from '@/config/routes';

interface IProps {
  clientResources: IClientRecentResources;
}

const ResourcesClientSection = ({ clientResources }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const history = useHistory();

  const createOnSeeAllHandler = (typeId: ResourceTypeId) => () => {
    history.push(
      generatePath(routes.sp.scheduling.resources.category, {
        client: clientResources.clientId,
        resourceType: ResourceTypeId[typeId],
      }),
    );
  };

  return (
    <Container>
      <SectionHeader>{clientResources.clientName}</SectionHeader>
      <CardsContainer>
        <ResourcesItemsCard
          title={t`resources.content.announcements`}
          iconSrc={ResourceAnnouncement}
          button={
            <ButtonSeeMore
              size="s"
              onClick={createOnSeeAllHandler(ResourceTypeId.announcement)}
            >{t`resources.home.seeAnnouncements`}</ButtonSeeMore>
          }
          items={clientResources.announcements}
          noDataLabel={t`resources.home.noAnnouncements`}
        />
        <ResourcesItemsCard
          title={t`resources.content.links`}
          iconSrc={ResourceLink}
          button={
            <ButtonSeeMore
              size="s"
              onClick={createOnSeeAllHandler(ResourceTypeId.link)}
            >{t`resources.home.seeLinks`}</ButtonSeeMore>
          }
          items={clientResources.links}
          noDataLabel={t`resources.home.noLinks`}
        />
        <ResourcesItemsCard
          title={t`resources.content.documents`}
          iconSrc={ResourceDocument}
          button={
            <ButtonSeeMore
              size="s"
              onClick={createOnSeeAllHandler(ResourceTypeId.document)}
            >{t`resources.home.seeFiles`}</ButtonSeeMore>
          }
          items={clientResources.documents}
          noDataLabel={t`resources.home.noFiles`}
        />
        <ResourcesDatabaseItemsCard
          title={t`resources.content.databases`}
          iconSrc={ResourceDatabase}
          button={
            <ButtonSeeMore
              size="s"
              onClick={createOnSeeAllHandler(ResourceTypeId.database)}
            >{t`resources.home.seeDatabases`}</ButtonSeeMore>
          }
          items={clientResources.databases}
          noDataLabel={t`resources.home.noDatabases`}
        />
      </CardsContainer>
    </Container>
  );
};

export { ResourcesClientSection };

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
`;

const SectionHeader = styled.h2`
  font-size: 24px;
  font-weight: 300;
  color: #0a4599;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 27px;
  row-gap: 34px;
  padding: 20px 0 34px;
  border-bottom: 1px solid #d1d5db;
  justify-content: center;
`;
